import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Lightbulb, TrendingUp, Clock, ExternalLink } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { StatusBadge } from '../components/StatusBadge';
interface ProductDetailProps {
  productId: number;
  onBack: () => void;
}
// Mock data generator based on ID
const getProductData = (id: number) => ({
  id,
  name: 'Smart TV Samsung 55" 4K UHD',
  category: 'Eletrônicos',
  price: 2899.0,
  marketAvg: 2750.0,
  status: 'acima' as const,
  lastUpdate: 'Há 2 horas',
  sku: 'SAM-55-4K-2024',
  image: 'https://images.unsplash.com/photo-1593784991067-94594287122b?auto=format&fit=crop&q=80&w=200&h=200'
});
const historyData = [{
  date: '01/06',
  price: 2999,
  market: 2950
}, {
  date: '08/06',
  price: 2999,
  market: 2900
}, {
  date: '15/06',
  price: 2899,
  market: 2850
}, {
  date: '22/06',
  price: 2899,
  market: 2800
}, {
  date: '29/06',
  price: 2899,
  market: 2750
}, {
  date: '06/07',
  price: 2899,
  market: 2750
}];
const competitorsData = [{
  name: 'Amazon BR',
  price: 2699.0,
  diff: -6.9,
  updated: '15 min atrás'
}, {
  name: 'Magazine Luiza',
  price: 2749.0,
  diff: -5.2,
  updated: '1h atrás'
}, {
  name: 'Casas Bahia',
  price: 2799.0,
  diff: -3.4,
  updated: '2h atrás'
}, {
  name: 'Fast Shop',
  price: 2999.0,
  diff: 3.4,
  updated: '4h atrás'
}, {
  name: 'Kabum',
  price: 2650.0,
  diff: -8.6,
  updated: '30 min atrás'
}];
export function ProductDetail({
  productId,
  onBack
}: ProductDetailProps) {
  const product = getProductData(productId);
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    duration: 0.3
  }} className="space-y-6" data-id="element-219">
      <button onClick={onBack} className="flex items-center text-slate-500 hover:text-navy-900 transition-colors font-medium" data-id="element-220">
        <ArrowLeft className="h-4 w-4 mr-2" data-id="element-221" />
        Voltar para Produtos
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-222">
        {/* Main Info Card */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-id="element-223">
          <div className="flex items-start justify-between" data-id="element-224">
            <div className="flex gap-4" data-id="element-225">
              <div className="h-24 w-24 bg-slate-100 rounded-lg flex-shrink-0 overflow-hidden" data-id="element-226">
                <img src={product.image} alt={product.name} className="h-full w-full object-cover" data-id="element-227" />
              </div>
              <div data-id="element-228">
                <div className="flex items-center gap-2 mb-1" data-id="element-229">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-230">
                    {product.category}
                  </span>
                  <StatusBadge status={product.status} data-id="element-231" />
                </div>
                <h1 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-232">
                  {product.name}
                </h1>
                <p className="text-sm text-slate-500" data-id="element-233">SKU: {product.sku}</p>
              </div>
            </div>
            <div className="text-right" data-id="element-234">
              <p className="text-sm text-slate-500 mb-1" data-id="element-235">Seu Preço</p>
              <p className="text-3xl font-bold text-navy-900" data-id="element-236">
                R${' '}
                {product.price.toLocaleString('pt-BR', {
                minimumFractionDigits: 2
              })}
              </p>
              <p className="text-sm text-red-500 font-medium mt-1" data-id="element-237">
                +5.4% vs Média
              </p>
            </div>
          </div>

          <div className="mt-8" data-id="element-238">
            <h3 className="text-lg font-semibold text-navy-900 mb-4" data-id="element-239">
              Histórico de Preços (30 dias)
            </h3>
            <div className="h-[300px] w-full" data-id="element-240">
              <ResponsiveContainer width="100%" height="100%" data-id="element-241">
                <LineChart data={historyData} data-id="element-242">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-243" />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fill: '#64748b'
                }} dy={10} data-id="element-244" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#64748b'
                }} data-id="element-245" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }} formatter={(value: number) => [`R$ ${value}`, '']} data-id="element-246" />
                  <Legend verticalAlign="top" height={36} data-id="element-247" />
                  <Line type="monotone" dataKey="price" name="Seu Preço" stroke="#1B2A4A" strokeWidth={3} dot={{
                  r: 4
                }} activeDot={{
                  r: 6
                }} data-id="element-248" />
                  <Line type="monotone" dataKey="market" name="Média de Mercado" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" dot={false} data-id="element-249" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6" data-id="element-250">
          {/* Suggestion Box */}
          <div className="bg-[#E8EDF2] border-l-4 border-navy-900 rounded-r-xl p-5 shadow-sm" data-id="element-251">
            <div className="flex items-start gap-3" data-id="element-252">
              <Lightbulb className="h-6 w-6 text-navy-900 flex-shrink-0 mt-0.5" data-id="element-253" />
              <div data-id="element-254">
                <h4 className="font-bold text-navy-900 mb-1" data-id="element-255">
                  Sugestão de Preço
                </h4>
                <p className="text-sm text-navy-800 leading-relaxed" data-id="element-256">
                  Considere ajustar o preço para{' '}
                  <span className="font-bold" data-id="element-257">R$ 2.749,00</span> para ficar
                  competitivo com a média do mercado e ganhar destaque no Buy
                  Box.
                </p>
                <button className="mt-3 text-xs font-bold text-navy-900 uppercase tracking-wide border-b border-navy-900 pb-0.5 hover:opacity-75" data-id="element-258">
                  Aplicar Sugestão
                </button>
              </div>
            </div>
          </div>

          {/* Market Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5" data-id="element-259">
            <h3 className="font-semibold text-navy-900 mb-4" data-id="element-260">
              Resumo do Mercado
            </h3>
            <div className="space-y-4" data-id="element-261">
              <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-262">
                <span className="text-sm text-slate-500" data-id="element-263">Média do Mercado</span>
                <span className="font-semibold text-navy-900" data-id="element-264">
                  R${' '}
                  {product.marketAvg.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2
                })}
                </span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-265">
                <span className="text-sm text-slate-500" data-id="element-266">Menor Preço</span>
                <span className="font-semibold text-emerald-600" data-id="element-267">
                  R$ 2.650,00
                </span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-268">
                <span className="text-sm text-slate-500" data-id="element-269">Maior Preço</span>
                <span className="font-semibold text-red-600" data-id="element-270">R$ 2.999,00</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-271">
                <span className="text-sm text-slate-500" data-id="element-272">Última Varredura</span>
                <span className="text-sm text-navy-900 flex items-center" data-id="element-273">
                  <Clock className="h-3 w-3 mr-1 text-slate-400" data-id="element-274" />
                  {product.lastUpdate}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Competitors Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-275">
        <div className="px-6 py-4 border-b border-slate-200" data-id="element-276">
          <h3 className="font-semibold text-navy-900" data-id="element-277">
            Comparativo de Concorrentes
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-278">
          <table className="w-full text-left text-sm" data-id="element-279">
            <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-280">
              <tr data-id="element-281">
                <th className="px-6 py-3" data-id="element-282">Concorrente</th>
                <th className="px-6 py-3" data-id="element-283">Preço</th>
                <th className="px-6 py-3" data-id="element-284">Diferença</th>
                <th className="px-6 py-3" data-id="element-285">Última Atualização</th>
                <th className="px-6 py-3" data-id="element-286">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-287">
              {competitorsData.map((comp, idx) => <tr key={idx} className="hover:bg-slate-50 transition-colors" data-id="element-288">
                  <td className="px-6 py-4 font-medium text-navy-900" data-id="element-289">
                    {comp.name}
                  </td>
                  <td className="px-6 py-4" data-id="element-290">
                    R${' '}
                    {comp.price.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2
                })}
                  </td>
                  <td className="px-6 py-4" data-id="element-291">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${comp.diff < 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-292">
                      {comp.diff > 0 ? '+' : ''}
                      {comp.diff}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-500" data-id="element-293">{comp.updated}</td>
                  <td className="px-6 py-4" data-id="element-294">
                    <button className="text-navy-600 hover:text-navy-900" data-id="element-295">
                      <ExternalLink className="h-4 w-4" data-id="element-296" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>;
}